import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import KpiCard from '@app/components/KpiCard';
import DataTable from '@app/components/DataTable';
import WatchlistDrawer from '@app/components/WatchlistDrawer';
import OrgModulePlaceholder from '@app/pages/org/OrgModulePlaceholder';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useVisibilityAwareRefresh } from '@app/hooks/useVisibilityAwareRefresh';
import { useConfig } from '@config/provider';
import { resolveBasePath } from '@app/utils/api';
import { buildOrgScopedPath } from '@app/utils/org-routes';
import { friendlyTargetLabel, formatSignalSubtype, signalSeverityBadgeClass, signalSeverityRank, signalStatusBadgeClass, signalStatusRank } from '@app/utils/signals';
import type { CursorListResponse, SiteSummary } from '@app/types/org-console';
import type { SignalConditionSummary, SignalIncidentSummary, SignalScopeType, SignalWatchlistItem } from '@app/types/signals';

interface SignalsSummaryProps {
  scopeType: SignalScopeType;
  scopeId: string;
  refreshSeconds?: number;
}

interface WatchlistDrawerScope {
  scopeType: SignalScopeType;
  scopeId: string;
}

const COCKPIT_AGGREGATE_LIMIT = 200;
// Overview cards may need more than one page, but must never fan out into an
// unbounded organization-wide crawl during initial render.
const COCKPIT_AGGREGATE_MAX_PAGES = 2;
const WATCHLIST_AGGREGATE_LIMIT = 2000;
const normalizeLabel = (value?: string | null): string => {
  const raw = (value ?? '').trim();
  if (!raw) {
    return 'Unknown';
  }
  return raw
    .replace(/[._]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

const compareUpdatedAtDesc = (left?: string | null, right?: string | null): number => {
  return new Date(right ?? 0).getTime() - new Date(left ?? 0).getTime();
};

const buildIncidentsSearch = (params: Record<string, string | null | undefined>): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (!value) {
      return;
    }
    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') {
      return;
    }
    search.set(key, trimmed);
  });
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
};

const buildQuery = (params: Record<string, string | number | null | undefined>) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === null || v === undefined) {
      return;
    }
    const s = String(v);
    if (s.length === 0) {
      return;
    }
    sp.set(k, s);
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
};

const resourceWithCursor = (resource: string, cursor: string): string => {
  const [path, rawQuery = ''] = resource.split('?');
  const params = new URLSearchParams(rawQuery);
  params.set('after', cursor);
  return `${path}?${params.toString()}`;
};

const fetchBoundedPages = async <T,>(
  fetchOrgCollection: (basePath: string, orgId: string, resource: string, init?: RequestInit) => Promise<CursorListResponse<T>>,
  basePath: string,
  orgId: string,
  resource: string
): Promise<CursorListResponse<T>> => {
  const items: T[] = [];
  let nextResource = resource;
  let nextCursor = '';
  let meta: CursorListResponse<T>['meta'] | undefined;
  for (let page = 0; page < COCKPIT_AGGREGATE_MAX_PAGES; page += 1) {
    const response = await fetchOrgCollection(basePath, orgId, nextResource, { cache: 'no-store' });
    items.push(...(response.items ?? []));
    meta = response.meta;
    nextCursor = response.meta?.next ?? '';
    if (!nextCursor) break;
    nextResource = resourceWithCursor(resource, nextCursor);
  }
  return {
    items,
    meta: {
      ...meta,
      limit: meta?.limit ?? COCKPIT_AGGREGATE_LIMIT,
      exactTotal: meta?.exactTotal ?? items.length,
      next: nextCursor || undefined
    }
  };
};

const SignalsSummary = ({ scopeType, scopeId, refreshSeconds = 0 }: SignalsSummaryProps) => {
  const orgId = useActiveOrgId();
  const { apiBaseUrl } = useConfig();
  const basePath = resolveBasePath(apiBaseUrl);
  const { fetchOrgCollection } = useAuthFetch();
  const navigate = useNavigate();
  const location = useLocation();

  const effectiveRefresh = refreshSeconds > 0 ? refreshSeconds : 0;
  const [refreshTick, setRefreshTick] = useState(0);
  const aggregateLimit = scopeType === 'org' ? COCKPIT_AGGREGATE_LIMIT : 10;

  useVisibilityAwareRefresh({
    intervalMs: effectiveRefresh * 1000,
    onRefresh: () => setRefreshTick((prev) => prev + 1)
  });

  const incidentsFetcher = useMemo(() => {
    if (!orgId || !scopeId) {
      return null;
    }
    const qs = buildQuery({ scopeType, scopeId, status: 'active', limit: aggregateLimit });
    return () => fetchBoundedPages<SignalIncidentSummary>(fetchOrgCollection, basePath, orgId, `signals/incidents${qs}`);
  }, [aggregateLimit, basePath, fetchOrgCollection, orgId, scopeId, scopeType, refreshTick]);

  const conditionsFetcher = useMemo(() => {
    if (!orgId || !scopeId) {
      return null;
    }
    return async (): Promise<CursorListResponse<SignalConditionSummary>> => {
      const qs = buildQuery({ scopeType, scopeId, family: 'all', state: 'ON', limit: aggregateLimit });
      return fetchBoundedPages<SignalConditionSummary>(fetchOrgCollection, basePath, orgId, `signals/conditions${qs}`);
    };
  }, [aggregateLimit, basePath, fetchOrgCollection, orgId, scopeId, scopeType, refreshTick]);

  const watchlistFetcher = useMemo(() => {
    if (!orgId || !scopeId) {
      return null;
    }
    const qs = buildQuery({ scopeType, scopeId, tier: 'hot', limit: scopeType === 'org' ? WATCHLIST_AGGREGATE_LIMIT : aggregateLimit });
    return () => fetchBoundedPages<SignalWatchlistItem>(fetchOrgCollection, basePath, orgId, `signals/watchlist${qs}`);
  }, [aggregateLimit, basePath, fetchOrgCollection, orgId, scopeId, scopeType, refreshTick]);

  const sitesFetcher = useMemo(() => {
    if (!orgId || scopeType !== 'org') {
      return null;
    }
    return () => fetchBoundedPages<SiteSummary>(fetchOrgCollection, basePath, orgId, `sites?limit=${COCKPIT_AGGREGATE_LIMIT}`);
  }, [basePath, fetchOrgCollection, orgId, scopeType, refreshTick]);

  const incidentsQuery = useApiQuery<CursorListResponse<SignalIncidentSummary>>(incidentsFetcher, [incidentsFetcher]);
  const conditionsQuery = useApiQuery<CursorListResponse<SignalConditionSummary>>(conditionsFetcher, [conditionsFetcher]);
  const watchlistQuery = useApiQuery<CursorListResponse<SignalWatchlistItem>>(watchlistFetcher, [watchlistFetcher]);
  const sitesQuery = useApiQuery<CursorListResponse<SiteSummary>>(sitesFetcher, [sitesFetcher]);

  const backendMissing = incidentsQuery.backendMissing || conditionsQuery.backendMissing;
  const anyError = incidentsQuery.error || conditionsQuery.error;

  const incidents = incidentsQuery.data?.items ?? [];
  const conditions = conditionsQuery.data?.items ?? [];
  const watchlist = watchlistQuery.data?.items ?? [];
  const rankedIncidents = useMemo(
    () =>
      [...incidents].sort(
        (left, right) =>
          signalSeverityRank(right.severity) - signalSeverityRank(left.severity) ||
          signalStatusRank(right.status) - signalStatusRank(left.status) ||
          compareUpdatedAtDesc(left.updatedAt ?? null, right.updatedAt ?? null)
      ),
    [incidents]
  );
  const sites = sitesQuery.data?.items ?? [];
  const siteNameById = useMemo(
    () =>
      sites.reduce<Record<string, string>>((acc, site) => {
        if (site.id) {
          acc[site.id] = site.name ?? site.id;
        }
        return acc;
      }, {}),
    [sites]
  );
  const incidentTotal = incidentsQuery.data?.meta?.exactTotal ?? incidents.length;
  const conditionsTotal = conditionsQuery.data?.meta?.exactTotal ?? conditions.length;
  const watchlistTotal = watchlistQuery.data?.meta?.exactTotal ?? watchlist.length;
  const overviewTruncated = Boolean(
    incidentsQuery.data?.meta?.next || conditionsQuery.data?.meta?.next || watchlistQuery.data?.meta?.next || sitesQuery.data?.meta?.next
  );
  const affectedSitesTotal = useMemo(
    () =>
      new Set(
        [...incidents, ...conditions]
          .filter((item) => item.scopeType === 'site' && item.scopeId)
          .map((item) => item.scopeId)
      ).size,
    [conditions, incidents]
  );

  const [watchlistOpen, setWatchlistOpen] = useState(false);
  const [watchlistScope, setWatchlistScope] = useState<WatchlistDrawerScope>({ scopeType, scopeId });
  const openWatchlist = useCallback(() => {
    setWatchlistScope({ scopeType, scopeId });
    setWatchlistOpen(true);
  }, [scopeId, scopeType]);
  const closeWatchlist = useCallback(() => setWatchlistOpen(false), []);
  const openIncidentsView = useCallback(
    (params: Record<string, string | null | undefined>) => {
      navigate(`/app/incidents${buildIncidentsSearch(params)}`, {
        state: { from: `${location.pathname}${location.search}` }
      });
    },
    [location.pathname, location.search, navigate]
  );
  const openConditionsExplorer = useCallback(
    (siteId: string) => {
      navigate(`/app/grafana?stage=conditions&family=all&siteId=${encodeURIComponent(siteId)}&state=ON`, {
        state: { from: `${location.pathname}${location.search}` }
      });
    },
    [location.pathname, location.search, navigate]
  );

  useEffect(() => {
    setWatchlistOpen(false);
  }, [scopeId, scopeType]);

  const severityRows = useMemo(
    () => [
      { key: 'crit', label: 'Critical', count: incidents.filter((incident) => (incident.severity ?? '').toLowerCase() === 'crit').length },
      { key: 'warn', label: 'Warning', count: incidents.filter((incident) => (incident.severity ?? '').toLowerCase() === 'warn').length },
      { key: 'unknown', label: 'Unknown', count: incidents.filter((incident) => !incident.severity || (incident.severity ?? '').toLowerCase() === 'unknown').length }
    ].filter((entry) => entry.count > 0 || incidentTotal === 0),
    [incidentTotal, incidents]
  );

  const subtypeRows = useMemo(() => {
    const counts = new Map<string, { raw: string; label: string; count: number }>();
    incidents.forEach((incident) => {
      const raw = (incident.subtype ?? '').trim();
      const key = raw || 'unknown';
      const existing = counts.get(key);
      if (existing) {
        existing.count += 1;
        return;
      }
      counts.set(key, { raw: key, label: normalizeLabel(incident.subtype), count: 1 });
    });
    return [...counts.values()]
      .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label))
      .slice(0, 5);
  }, [incidents]);

  const conditionRows = useMemo(() => {
    const counts = new Map<string, number>();
    conditions.forEach((condition) => {
      const key = normalizeLabel((condition as any).conditionKey ?? (condition as any).condition ?? 'Unknown');
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return [...counts.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label))
      .slice(0, 5);
  }, [conditions]);

  const activeConditionRows = useMemo(() => {
    const rows = new Map<
      string,
      {
        id: string;
        conditionKey: string;
        targetLabel: string;
        state: string;
        count: number;
        latestAt: string | null;
      }
    >();

    conditions.forEach((condition) => {
      const conditionKey = ((condition as any).conditionKey ?? (condition as any).condition ?? 'Unknown') as string;
      const targetId = ((condition as any).targetId ?? '') as string;
      const targetLabel = friendlyTargetLabel(targetId || null, (condition as any).attributes ?? null);
      const id = `${conditionKey}\u0000${targetId || targetLabel}`;
      const latestAt = ((condition as any).updatedAt ?? (condition as any).observedAt ?? (condition as any).createdAt ?? null) as string | null;
      const state = condition.state ?? '—';
      const existing = rows.get(id);

      if (existing) {
        existing.count += 1;
        if (compareUpdatedAtDesc(existing.latestAt, latestAt) > 0) {
          existing.latestAt = latestAt;
          existing.state = state;
        }
        return;
      }

      rows.set(id, {
        id,
        conditionKey,
        targetLabel,
        state,
        count: 1,
        latestAt
      });
    });

    return [...rows.values()]
      .sort((left, right) => {
        const countDiff = right.count - left.count;
        if (countDiff !== 0) {
          return countDiff;
        }
        const timeDiff = compareUpdatedAtDesc(left.latestAt, right.latestAt);
        if (timeDiff !== 0) {
          return timeDiff;
        }
        return left.conditionKey.localeCompare(right.conditionKey) || left.targetLabel.localeCompare(right.targetLabel);
      })
      .slice(0, 5);
  }, [conditions]);

  const hotspotRows = useMemo(() => {
    const counts = new Map<string, { siteId: string; incidents: number; conditions: number }>();
    const ensure = (siteId: string) => {
      if (!counts.has(siteId)) {
        counts.set(siteId, { siteId, incidents: 0, conditions: 0 });
      }
      return counts.get(siteId)!;
    };

    incidents.forEach((incident) => {
      if (incident.scopeType !== 'site' || !incident.scopeId) {
        return;
      }
      ensure(incident.scopeId).incidents += 1;
    });

    conditions.forEach((condition) => {
      if (condition.scopeType !== 'site' || typeof condition.scopeId !== 'string' || !condition.scopeId) {
        return;
      }
      ensure(condition.scopeId).conditions += 1;
    });

    return [...counts.values()]
      .sort((left, right) => {
        const impactDiff = right.incidents - left.incidents;
        if (impactDiff !== 0) {
          return impactDiff;
        }
        const conditionDiff = right.conditions - left.conditions;
        if (conditionDiff !== 0) {
          return conditionDiff;
        }
        return (siteNameById[left.siteId] ?? left.siteId).localeCompare(siteNameById[right.siteId] ?? right.siteId);
      })
      .slice(0, 5)
      .map((row) => ({
        ...row,
        siteLabel: siteNameById[row.siteId] ?? row.siteId
      }));
  }, [conditions, incidents, siteNameById]);

  const dominantSubtype = subtypeRows[0] ?? null;
  const criticalCount = severityRows.find((entry) => entry.key === 'crit')?.count ?? 0;
  const attentionSiteRows = hotspotRows.map((row) => ({
    ...row,
    summaryParts: [
      `${row.incidents} active incident${row.incidents === 1 ? '' : 's'}`,
      row.conditions > 0 ? `${row.conditions} condition${row.conditions === 1 ? '' : 's'} on` : null
    ].filter(Boolean) as string[]
  }));
  const topImpactedSiteCount = Math.min(5, attentionSiteRows.length);
  const topConditionTypeCount = Math.min(5, conditionRows.length);
  const topConditionRowCount = Math.min(5, activeConditionRows.length);

  if (backendMissing) {
    return (
      <OrgModulePlaceholder intent="Implement signals read APIs in api-server/promon-service to populate incidents/conditions/watchlist widgets." />
    );
  }

  return (
    <div className="signals-summary">
      <div className="signals-summary__kpis kpi-grid">
        <KpiCard
          label="Active incidents"
          value={incidentsQuery.loading ? '…' : String(incidentTotal)}
          secondary={`${scopeType}`}
          onClick={() => openIncidentsView({ status: 'active', scopeMode: scopeType === 'site' ? 'site' : null, siteId: scopeType === 'site' ? scopeId : null })}
        />
        <KpiCard label="Conditions ON" value={conditionsQuery.loading ? '…' : String(conditionsTotal)} secondary={`${scopeType}`} />
        {scopeType === 'org' ? (
          <>
            <KpiCard
              label="Sites affected"
              value={incidentsQuery.loading || conditionsQuery.loading ? '…' : String(affectedSitesTotal)}
              secondary="sites"
            />
            <KpiCard
              label="Watchlist (hot)"
              value={watchlistQuery.loading ? '…' : String(watchlistTotal)}
              secondary="open detail"
              onClick={openWatchlist}
            />
          </>
        ) : (
          <KpiCard
            label="Watchlist (hot)"
            value={watchlistQuery.loading ? '…' : String(watchlistTotal)}
            secondary={`${scopeType}`}
            onClick={openWatchlist}
          />
        )}
      </div>

      {anyError ? <p className="org-shell__banner">{anyError.message}</p> : null}
      {overviewTruncated ? (
        <p className="org-shell__banner">Showing the latest bounded overview page; KPI totals include the full backend count.</p>
      ) : null}

      {scopeType === 'org' ? (
        <div className="signals-summary__insights">
          <div className="signals-summary__panel">
            <div className="signals-summary__panel-header">
              <div>
                <h3 className="signals-summary__title">Incident focus</h3>
                <p className="signals-summary__subtitle">Dominant active incident patterns right now.</p>
              </div>
            </div>
            <button
              type="button"
              className="signals-summary__focus-callout"
              onClick={() => openIncidentsView({ status: 'active', subtype: dominantSubtype?.raw === 'unknown' ? '' : dominantSubtype?.raw ?? undefined })}
            >
              <span className="signals-summary__focus-eyebrow">Primary issue</span>
              <strong>{dominantSubtype?.label ?? 'No active incident subtype'}</strong>
              <span className="signals-summary__focus-meta">
                {dominantSubtype ? `${dominantSubtype.count} active incident${dominantSubtype.count === 1 ? '' : 's'}` : 'No active incidents'}
              </span>
            </button>
            <div className="signals-summary__inline-stats">
              <button
                type="button"
                className="signals-summary__inline-stat"
                onClick={() => openIncidentsView({ status: 'active', severity: 'crit' })}
              >
                <span>Critical</span>
                <strong>{criticalCount}</strong>
              </button>
              <span className="signals-summary__inline-stat signals-summary__inline-stat--static">
                <span>Open subtypes</span>
                <strong>{subtypeRows.length}</strong>
              </span>
            </div>
            {subtypeRows.length > 1 ? (
              <div className="signals-summary__stat-list">
                {subtypeRows.slice(1).map((entry) => (
                  <button
                    key={entry.raw}
                    type="button"
                    className="signals-summary__stat-row"
                    onClick={() => openIncidentsView({ status: 'active', subtype: entry.raw === 'unknown' ? '' : entry.raw })}
                  >
                    <span>{entry.label}</span>
                    <strong>{entry.count}</strong>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="signals-summary__panel">
            <div className="signals-summary__panel-header">
              <div>
                <h3 className="signals-summary__title">Sites needing attention</h3>
                <p className="signals-summary__subtitle">
                  {topImpactedSiteCount > 0
                    ? `Top ${topImpactedSiteCount} impacted site${topImpactedSiteCount === 1 ? '' : 's'} by active incidents or conditions.`
                    : 'No site hotspots right now.'}
                </p>
              </div>
            </div>
            {attentionSiteRows.length === 0 ? (
              <p className="signals-summary__empty">No site hotspots right now.</p>
            ) : (
              <div className="signals-summary__hotspots">
                {attentionSiteRows.map((row) => (
                  <div key={row.siteId} className="signals-summary__hotspot-row">
                    <div className="signals-summary__hotspot-header">
                      <strong>{row.siteLabel}</strong>
                      <span className="signals-summary__hotspot-count">{row.incidents + row.conditions}</span>
                    </div>
                    <div className="signals-summary__hotspot-meta">
                      {row.summaryParts.join(' • ')}
                    </div>
                    <div className="signals-summary__chip-row">
                      <button
                        type="button"
                        className="signals-summary__chip"
                        onClick={() => openIncidentsView({ scopeMode: 'site', siteId: row.siteId, status: 'active' })}
                        disabled={row.incidents === 0}
                      >
                        Incidents {row.incidents}
                      </button>
                      <button
                        type="button"
                        className="signals-summary__chip"
                        onClick={() => openConditionsExplorer(row.siteId)}
                        disabled={row.conditions === 0}
                      >
                        Conditions {row.conditions}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="signals-summary__panel">
            <div className="signals-summary__panel-header">
              <div>
                <h3 className="signals-summary__title">
                  {topConditionTypeCount > 0 ? `Top ${topConditionTypeCount} active condition types` : 'Active condition types'}
                </h3>
                <p className="signals-summary__subtitle">Most common active L2 condition types across the selected scope.</p>
              </div>
            </div>
            {conditionRows.length === 0 ? (
              <p className="signals-summary__empty">No active supporting conditions.</p>
            ) : (
              <div className="signals-summary__stat-list">
                {conditionRows.map((entry) => (
                  <div key={entry.label} className="signals-summary__stat-row signals-summary__stat-row--static">
                    <span>{entry.label}</span>
                    <strong>{entry.count}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      <div className="signals-summary__tables">
        <div className="signals-summary__table">
          <h3 className="signals-summary__title">Incidents</h3>
          <p className="signals-summary__subtitle">Operator-facing active problems that need action.</p>
          {incidentsQuery.loading && incidents.length === 0 ? <p className="org-shell__banner">Loading incidents…</p> : null}
          {incidents.length === 0 && !incidentsQuery.loading ? <p className="signals-summary__empty">No open incidents.</p> : null}
          {incidents.length > 0 ? (
            <DataTable
              rows={rankedIncidents.slice(0, 5)}
              columns={[
                {
                  key: 'title',
                  header: 'Title',
                  render: (row) => {
                    const maybeTitle = (row as any).title as string | null | undefined;
                    if (maybeTitle && maybeTitle.trim().length) {
                      return (
                        <div className="signals-summary__incident-cell">
                          <strong>{maybeTitle.trim()}</strong>
                          <span className="signals-summary__incident-meta">{friendlyTargetLabel((row as any).targetId ?? null, (row as any).attributes ?? null)}</span>
                        </div>
                      );
                    }
                    const label = friendlyTargetLabel((row as any).targetId ?? null, (row as any).attributes ?? null);
                    return (
                      <div className="signals-summary__incident-cell">
                        <strong>{(row as any).conditionKey ? `${formatSignalSubtype((row as any).conditionKey)} • ${label}` : row.id}</strong>
                        <span className="signals-summary__incident-meta">{formatSignalSubtype((row as any).subtype ?? 'incident')}</span>
                      </div>
                    );
                  }
                },
                {
                  key: 'severity',
                  header: 'Severity',
                  render: (row) => <span className={signalSeverityBadgeClass(row.severity)}>{(row.severity ?? 'unknown').toUpperCase()}</span>
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (row) => <span className={signalStatusBadgeClass(row.status)}>{(row.status ?? 'unknown').toUpperCase()}</span>
                }
              ]}
              onRowClick={(row) => {
                if (!orgId) {
                  return;
                }
                navigate(buildOrgScopedPath(`/app/incidents/${encodeURIComponent(row.id)}`, orgId), {
                  state: { from: `${location.pathname}${location.search}` }
                });
              }}
              rowId={(row) => row.id}
            />
          ) : null}
        </div>

        <div className="signals-summary__table">
          <h3 className="signals-summary__title">{topConditionRowCount > 0 ? `Top ${topConditionRowCount} active conditions` : 'Conditions'}</h3>
          <p className="signals-summary__subtitle">Grouped active L2 conditions by condition and target for the selected scope.</p>
          {conditionsQuery.loading && conditions.length === 0 ? <p className="org-shell__banner">Loading conditions…</p> : null}
          {activeConditionRows.length === 0 && !conditionsQuery.loading ? <p className="signals-summary__empty">No active conditions.</p> : null}
          {activeConditionRows.length > 0 ? (
            <DataTable
              rows={activeConditionRows}
              columns={[
                {
                  key: 'conditionKey',
                  header: 'Condition',
                  render: (row) => row.conditionKey
                },
                {
                  key: 'targetLabel',
                  header: 'Target',
                  render: (row) => row.targetLabel
                },
                { key: 'state', header: 'State', render: (row) => row.state },
                { key: 'count', header: 'Count', render: (row) => row.count }
              ]}
              rowId={(row) => row.id}
            />
          ) : null}
        </div>
      </div>

      <WatchlistDrawer open={watchlistOpen} scopeType={watchlistScope.scopeType} scopeId={watchlistScope.scopeId} tier="hot" onClose={closeWatchlist} />
    </div>
  );
};

export default SignalsSummary;
